import { useCallback, useMemo, useRef } from "react";

import { TableBody } from "@mui/material";

import { useAppPalette } from "../../../theme";
import { LayoutContext, LayoutContextValue, SelectionContext, SelectionContextValue } from "../contexts";
import { GroupingProp } from "../tableProps";
import { RowCheckboxState } from "../useSelection";
import { TableColumn, TableKey, TableRow } from "../types";
import { GroupRow } from "./GroupRow";
import { ItemRows } from "./ItemRows";

interface GroupedBodyProps<TRow, TDetail> {
    visibleRows: TableRow<TRow>[];
    columns: TableColumn<TRow>[];
    grouping: GroupingProp<TDetail>;
    rowCheckboxByKey: Map<TableKey, RowCheckboxState>;
    selectionContextValue: SelectionContextValue | null;
    expandedGroups: ReadonlySet<TableKey>;
    setGroupsExpanded: (keys: TableKey[], expanded: boolean) => void;
}

/**
 * Grouped-mode `<TableBody>`. Mounts `LayoutContext` (stable) and conditionally
 * `SelectionContext` (volatile). The split is the perf-cliff fix: layout-reading
 * descendants (skeleton cells, divider sizing) don't re-render when selection
 * state moves.
 */
export function GroupedBody<TRow, TDetail>({
    visibleRows,
    columns,
    grouping,
    rowCheckboxByKey,
    selectionContextValue,
    expandedGroups,
    setGroupsExpanded
}: GroupedBodyProps<TRow, TDetail>) {
    const { divider, headerBackground, detailBackground, hoverBackground } = useAppPalette();

    const hasSelection = !!selectionContextValue;
    const leadingCells: 1 | 2 = hasSelection ? 2 : 1;

    // Consumers pass `onDetailRowNavigatePath` as an inline arrow, so its reference changes every
    // render. Funnel it through a ref + stable callback so `layoutValue` stays referentially stable
    // and layout-reading descendants don't re-render on unrelated parent renders.
    const onDetailRowNavigatePathRef = useRef(grouping.onDetailRowNavigatePath);
    onDetailRowNavigatePathRef.current = grouping.onDetailRowNavigatePath;
    const stableOnDetailRowNavigatePath = useCallback(
        (item: unknown) => onDetailRowNavigatePathRef.current?.(item as TDetail),
        []
    );

    const layoutValue = useMemo<LayoutContextValue>(
        () => ({
            summaryColumnCount: columns.length,
            leadingCells,
            divider,
            headerBackground,
            detailBackground,
            hoverBackground,
            onDetailRowNavigatePath: stableOnDetailRowNavigatePath
        }),
        [
            columns.length,
            leadingCells,
            divider,
            headerBackground,
            detailBackground,
            hoverBackground,
            stableOnDetailRowNavigatePath
        ]
    );

    const body = (
        <TableBody>
            {visibleRows.map((row, i) => (
                <GroupRow<TRow>
                    key={row.key}
                    row={row}
                    rowIndex={i}
                    columns={columns}
                    checkbox={hasSelection ? rowCheckboxByKey.get(row.key) : undefined}
                    isExpanded={expandedGroups.has(row.key)}
                    setIsExpanded={(v) => setGroupsExpanded([row.key], v)}
                    divider={divider}
                    itemBlock={
                        <ItemRows<TDetail>
                            columns={grouping.columns}
                            items={grouping.getItems(row.key) ?? []}
                            isLoading={!!grouping.isDetailsLoading}
                            getDetailKey={grouping.getDetailKey}
                        />
                    }
                />
            ))}
        </TableBody>
    );

    return (
        <LayoutContext.Provider value={layoutValue}>
            {selectionContextValue ? (
                <SelectionContext.Provider value={selectionContextValue}>{body}</SelectionContext.Provider>
            ) : (
                body
            )}
        </LayoutContext.Provider>
    );
}
